import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../apiClient';
import { ErrorBanner } from './Configuration';
import { MultiSelect } from './registerFilters';
import { timeZoneNote } from './BPATracker';
import { CalendarSurface, EVENT_TYPES, toCalendarEvents } from './ProjectCalendar';
import ProjectEventModal from './ProjectEventForm';
import { useApp } from '../appContext';

// Same gate as the portal's BPA nav item: with BPA switched off there is no /bpa screen to send
// anyone to, so a session's detail card offers no "View".
const BPA_ENABLED = process.env.REACT_APP_BPA_ENABLED === 'true';

/**
 * The customer portal's copy of the Project Calendar — one project at a time.
 *
 * The calendar itself is `CalendarSurface`, the same component the internal page renders, so the
 * event colors, the month/week pair and the detail card can't drift between the two views (the
 * argument CustomerProjectRegisters.js makes for sharing its grids). What differs here is only what
 * has to:
 *
 * - **One project.** `GET /calendar/events/:projectId` carries @AllowExternal, so the API checks
 *   this contact's ExternalUserProject grant before answering — the picker below is a convenience,
 *   never the boundary. It appears at all only when a contact has been granted more than one.
 * - **No project-color dots.** They exist to tell several projects apart, which can't arise here.
 * - **Times are the county's own.** The API sends this view's times floating, with no zone
 *   (`CalendarEvent.start`), so a 9:30 session reads as 9:30 for the contact and for a PM
 *   previewing the portal from another zone alike. The note above the calendar names the zone
 *   once, the way ADR 0019 does above the BPA grid, rather than repeating it in every event.
 *
 * The two per-row exclusions on RAAIDD data — `internalOnly` and removed rows — are enforced by
 * CalendarService, not by anything here.
 *
 * **Creating an event** (item 5, 2026-09-12): a contact with edit rights on the selected project
 * (`editableProjectIds` from `/auth/me`) gets the same "+ Add Project Event" modal ProjectCalendar.js
 * uses — `ProjectEventsController` enforces the grant server-side regardless of what this page shows.
 */
const PORTAL_LINK_BY_TYPE = {
  milestone: '/schedule',
  phase: '/schedule',
  task: '/schedule',
  bpa: '/bpa',
  raaidd: '/registers?tab=raaidd',
  change: '/registers?tab=change',
  bau: '/registers?tab=bau',
  event: '/registers?tab=events',
};

export default function CustomerProjectCalendar() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [vocabulary, setVocabulary] = useState(null);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);
  const { currentUser } = useApp();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The zone note needs the states lookup, which is served rather than duplicated in the
      // browser (ADR 0019). A failure there must not cost us the calendar, so it's allowed to
      // settle on its own — the note simply doesn't render.
      const [projectResult, vocabResult] = await Promise.allSettled([
        apiFetch('/projects'),
        apiFetch('/bpa/vocabulary'),
      ]);
      if (cancelled) return;
      if (projectResult.status === 'rejected') {
        setError(projectResult.reason.message);
        setLoading(false);
        return;
      }
      const rows = projectResult.value;
      setProjects(rows);
      // The common case: a contact is granted exactly one project, so make them pick nothing.
      if (rows.length === 1) setProjectId(rows[0].id);
      if (vocabResult.status === 'fulfilled') setVocabulary(vocabResult.value);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // A sequence number rather than a per-effect `cancelled` flag: `loadEvents` is also called
  // directly from the "+ Add Project Event" modal's `onSaved`, outside the effect below, so a plain
  // closure-captured flag wouldn't cover a race between that manual call and an in-flight one.
  const loadSeq = useRef(0);
  const loadEvents = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!projectId) {
      setEvents([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (selectedTypes.length) params.set('types', selectedTypes.join(','));
      const query = params.toString();
      const rows = await apiFetch(`/calendar/events/${projectId}${query ? `?${query}` : ''}`);
      if (seq !== loadSeq.current) return;
      setEvents(toCalendarEvents(rows));
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e.message);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [projectId, selectedTypes]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const canEdit = !!projectId && !!currentUser?.editableProjectIds?.includes(projectId);
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === projectId),
    [projects, projectId],
  );

  const zoneNote = useMemo(() => {
    const code = (selectedProject?.account?.state ?? '').trim().toUpperCase();
    return timeZoneNote((vocabulary?.states ?? []).find((s) => s.code === code));
  }, [selectedProject, vocabulary]);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Project Calendar</div>
        <h1 className="page-title">Project Calendar</h1>
        <div className="page-subtitle">
          Everything scheduled on your project — milestones, phases, tasks, BPA sessions and the
          dates from your registers — on one calendar.
        </div>
      </div>

      <ErrorBanner error={error} />

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Only worth a control when there's a choice to make. */}
          {projects.length > 1 && (
            <div className="form-group" style={{ marginBottom: 0, minWidth: 280, maxWidth: 340 }}>
              <label className="form-label" htmlFor="customer-calendar-project">Project</label>
              <select
                id="customer-calendar-project"
                className="form-select"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">Select a project…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          <MultiSelect
            label="Event types"
            options={EVENT_TYPES}
            selected={selectedTypes}
            onChange={setSelectedTypes}
            allLabel="All types"
            minWidth={220}
            showSelectAll
          />
          {canEdit && (
            <button className="btn btn-outline" onClick={() => setAddingEvent(true)}>+ Add Project Event</button>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: 10 }}>
          {zoneNote
            ? <>Times are <strong>{zoneNote.zone}</strong> — {zoneNote.stateName}, your own local time.</>
            : <>Times are your project's own local time.</>}
          {zoneNote?.caveats.map((c) => (
            <span key={c} style={{ display: 'block', color: 'var(--text-light)' }}>{c}</span>
          ))}
        </div>
      </div>

      {!projectId ? (
        <div className="placeholder-page">
          <div className="placeholder-title">No project selected</div>
          <div className="placeholder-sub">Choose a project above to see its calendar.</div>
        </div>
      ) : loading ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-mid)' }}>
          Loading calendar…
        </div>
      ) : (
        <CalendarSurface
          events={events}
          linkFor={(event) => {
            const path = PORTAL_LINK_BY_TYPE[event.type];
            if (path === '/bpa' && !BPA_ENABLED) return null;
            return path ?? null;
          }}
        />
      )}

      {addingEvent && selectedProject && (
        <ProjectEventModal
          projects={[selectedProject]}
          initialProjectId={selectedProject.id}
          onClose={() => setAddingEvent(false)}
          onSaved={loadEvents}
          portal
        />
      )}
    </div>
  );
}
